import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';

import { NS } from '@/constants';

import { useAppDispatch, useAppSelector } from '@/redux';
import { addCartProduct } from '@/redux/slices/cart.slice';

import {
  getAttribute,
  getAttributeValue,
  getProductImage, isItemLightPriceUSA,
} from '@/utils/productUtils';
import { getCriticsRatingNotes, getProductPageRedirectionInfos } from '@/utils/productCardUtils';
import { getProductModel } from '@/utils/itemSelectorUtils';
import { isObjectEqual } from '@/utils/comparisonUtils';
import { ATTRIBUTES } from '@/constants/product';
import { AttributeStringModel } from '@/models';

import ProductImage from '@/components/product/ProductImage';
import ProductCardName from '@/components/listing/ProductCardName';
import Dropdown from '@/components/common/design-system/Dropdown.ds';
import WishListIcon from '@/components/account/wishList/WishListIcon';
import MillesimaPrice from '@/components/common/price/MillesimaPrice';
import ProductTagList from '@/components/common/badge/ProductTagList';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import AvailabilityIndicator from '@/components/common/AvailabilityIndicator';
import ProductCardAttributes from '@/components/listing/ProductCardAttributes';
import ProductRatingList from '@/components/common/product/rating/ProductRatingList';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';

import styles from './ProductCardCU.module.scss';

export const ProductCardCU = ({ product, index, currentItem, isCU }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const router = useRouter();

  const isMobile = useScreenSizeContext().isMobile;
  const storeConf = useAppSelector(({ root }) => root.storeConf);
  const [currentItem2, setCurrentItem2] = useState(currentItem);

  const { image, productName } = product;
  const itemCu = currentItem2;
  const { stock, partnumber } = itemCu;
  const promotion: AttributeStringModel = getAttribute(itemCu, ATTRIBUTES.RIBBONAD);
  const infoSiteLabel = getAttributeValue(itemCu, ATTRIBUTES.INFO_SITE);

  const quantities = stock ? Array(Math.min(stock, 18)).fill(1).map((_, i) => ({ value: i + 1, label: i + 1 })) : [];

  const [quantity, setQuantity] = useState<string | number>(1);

  const url = getProductImage(image, false, isMobile ? 'mobile' : 'thumb');
  const ratings = getCriticsRatingNotes(product, storeConf) || [];

  const productAttributes = {
    region: getAttribute(product, ATTRIBUTES.REGION),
    appellation: getAttribute(product, ATTRIBUTES.APPELLATION),
    couleur: getAttributeValue(product, ATTRIBUTES.COLOR, ''),
    isSpirit: product?.isSpirit,
    storeConf
  };

  useEffect(() => {
    setCurrentItem2(currentItem);
  }, [currentItem]);

  const handleClickProduct = () => {
    const { href, as } = getProductPageRedirectionInfos(product, itemCu, storeConf);
    router.push(href, as);
  };

  const addToCart = () => {
    dispatch(addCartProduct({
      item: partnumber,
      qty: Number(quantity),
      error: undefined,
      options: {
        set: false
      }
    }));
    setQuantity(1);
  };

  const buttonLabel: string = stock === 0 ? t('product.noStock') :
    isMobile ? t('productCard.quickView', { ns: NS.CATEG }) :
      t('product.addToCart');

  const buttonProps: any = {
    disabled: stock === 0,
    onClick: addToCart,
    id: 'add-to-cart',
    buttonSize: 'small',
    className: styles.button
  };

  const capacity = { value: partnumber, label: product.shortDesc };
  const productModel = getProductModel(product);

  const getDropdownOptions = () => {
    return productModel.itemsList.filter(({ offerPrice, listPrice }) => listPrice > 0 || offerPrice > 0).map(formatOption);
  };

  const formatOption = ({ listPrice, offerPrice, shortdesc, partnumber }) => {
    const priceToFormat = offerPrice > 0 ? offerPrice : listPrice;

    const price = MillesimaPrice.format(t, {
      value: priceToFormat,
      locale: storeConf.locale,
      currency: storeConf.currencyISO
    });

    return {
      value: partnumber,
      label: `${shortdesc} - ${price}`
    };
  };

  const onItemChange = (partnumber) => {
    const item = product.items?.find((i) => i.partnumber === partnumber) ||
      product.items[0] || {};

    if (!isObjectEqual(currentItem, item)) {
      setCurrentItem2(item);
    }
  };

  return (
    <>
      {index <= 3 && (
        <Head>
          <link rel="preload" href={url} as="image" />
        </Head>
      )}
      <div className={styles.container}>
        <div className={styles.header} onClick={handleClickProduct} data-test-id="header">
          <div className={styles['rating-and-wishlist']}>
            <WishListIcon product={{ ...product, partnumber: partnumber.split('/').slice(0, 2).join('/') }} />
            <ProductRatingList ratings={ratings} ratingMaxNumber={isMobile ? 1 : 3}/>
          </div>
          <ProductImage
            customClassName={styles['product-img']}
            url={url}
            alt={productName}
            position={index}
          />
        </div>
        <div className={styles.content}>
          <ProductTagList
            t={t}
            promotion={promotion}
            infoSiteLabel={infoSiteLabel}
            lightPrice={isItemLightPriceUSA(currentItem, storeConf.storeId)}
          />
          <ProductCardName index={index} product={product} currentItem={itemCu} storeConf={storeConf} />
          <ProductCardAttributes {...productAttributes} index={index} />
        </div>
        <div className={styles.footer} data-test-id="footer">
          <MillesimaPrice className={{ container: styles['price-container'], value: styles.price }} value={itemCu.offerPrice} />
          <div className={styles['footer-container']}>
            {!isCU && (
              <Dropdown
                id="item-selector"
                customClassName={styles['item-selector']}
                value={capacity?.value}
                options={getDropdownOptions()}
                onChange={(partnumber: string) => onItemChange(partnumber)}
                isDisabled={productModel.itemsList.length === 0}
                size="small"
                isReadOnly={product.items?.length === 1}
              />
            )}
            {itemCu?.stock > 0 && quantities.length > 0 && !isMobile && (
              <Dropdown
                value={quantity}
                options={quantities}
                onChange={(value) => setQuantity(value)}
                size="small"
              />
            )}
            <CallToActionContainer {...buttonProps} labelClassName={styles.label} sensefuelId="add-to-cart" fullWidth >
              {buttonLabel}
            </CallToActionContainer>
          </div>
          <AvailabilityIndicator
            t={t}
            product={product}
            currentItem={itemCu}
            storeId={storeConf.storeId}
            locale={storeConf.locale}
            customClassName={styles['availability-indicator']}
          />
        </div>
      </div>
    </>
  );
};

export default ProductCardCU;
